/**
 * ErrorBoundary - catches render errors in its subtree so one broken component
 * (e.g. a malformed chart from an AI reply) shows a fallback instead of crashing
 * the whole app to a white screen.
 *
 * React only catches errors with class components, so this stays a class.
 * Pass `resetKeys` to auto-recover when the user navigates elsewhere (e.g. the
 * active conversation/tab changes) — otherwise a crash sticks until reload.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

type FallbackRender = (error: Error, reset: () => void) => ReactNode;

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | FallbackRender;
  onError?: (error: Error, info: ErrorInfo) => void;
  resetKeys?: unknown[];
}

interface ErrorBoundaryState {
  error: Error | null;
}

function arraysDiffer(a?: unknown[], b?: unknown[]): boolean {
  if (a === b) return false;
  if (!a || !b || a.length !== b.length) return true;
  return a.some((val, i) => !Object.is(val, b[i]));
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, info);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && arraysDiffer(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === 'function') return fallback(error, this.reset);
    if (fallback !== undefined) return fallback;
    return <DefaultFallback error={error} reset={this.reset} />;
  }
}

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="max-w-md">
        <h2 className="mb-2 text-lg font-semibold text-[var(--color-text-primary)]">
          Something went wrong
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)]">
          This view hit an unexpected error and couldn't be displayed.
        </p>
        {error.message ? (
          <p className="mt-2 break-words font-mono text-xs text-[var(--color-text-muted)]">
            {error.message}
          </p>
        ) : null}
      </div>
      <div className="flex gap-3">
        <button
          onClick={reset}
          className="rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          Try again
        </button>
        <button
          onClick={() => window.location.assign('/')}
          className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]"
        >
          Go home
        </button>
      </div>
    </div>
  );
}
